import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";

import {
    fetchAccountPlanMap,
    fetchPlanAccumulatorSetup,
    fetchPlanCoverage,
    fetchPlanRiderDetail,
    fetchPlanRiders,
} from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import { mergeProvisionSources, PlanView } from "./PlanCoverageView";
import { inferSlotName } from "../../utils/slotBrowse";
import "./Coverage.scss";

const riderIndex = (coverage) => {
    const byName = new Map();
    for (const rider of coverage?.riders?.chosen || []) {
        if (rider.name) byName.set(String(rider.name).toLowerCase(), rider);
        if (rider.hccId) byName.set(String(rider.hccId).toLowerCase(), rider);
    }
    return byName;
};

const sourcesForComponent = (component, ridersByName) => {
    const name = component?.name;
    if (!name) return [];
    const rider = ridersByName.get(String(name).toLowerCase());
    if (rider) return [{ kind: "rider", name: rider.name || name }];
    return [{ kind: "component", name }];
};

const planOptionLabel = (plan) => {
    const name = plan?.name || "Plan";
    const hcc = plan?.hccId ? ` (${plan.hccId})` : "";
    const status = plan?.statusLabel ? ` · ${plan.statusLabel}` : "";
    return `${name}${hcc}${status}`;
};

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const normalizeProvisionText = (text) => String(text || "").replace(/\s+/g, " ").trim();

const provisionDedupeKey = (p) =>
    [
        normalizeProvisionText(p?.text),
        p?.type || "",
        p?.tierKey ?? p?.tierName ?? "",
        p?.isInNetwork ?? "",
    ].join("\0");

const specificityScore = (p) =>
    (p?.type ? 1 : 0) + (p?.tierKey || p?.tierName ? 1 : 0) + (p?.isInNetwork != null ? 1 : 0);

/** True when a looser row (no type / tier / network) is the same rule as a more specific one. */
const isSameProvisionRule = (a, b) => {
    if (normalizeProvisionText(a?.text) !== normalizeProvisionText(b?.text)) return false;
    if (a?.type && b?.type && a.type !== b.type) return false;
    const aTier = a?.tierKey ?? a?.tierName ?? "";
    const bTier = b?.tierKey ?? b?.tierName ?? "";
    if (aTier && bTier && String(aTier) !== String(bTier)) return false;
    if (a?.isInNetwork != null && b?.isInNetwork != null && Boolean(a.isInNetwork) !== Boolean(b.isInNetwork)) {
        return false;
    }
    return true;
};

const addUniqueProvision = (list, provision, incomingSources) => {
    const sources = mergeProvisionSources(provision.sources, incomingSources);
    const next = { ...provision, sources, source: sources[0]?.name };
    const existingIndex = list.findIndex(
        (row) => provisionDedupeKey(row) === provisionDedupeKey(next) || isSameProvisionRule(row, next),
    );
    if (existingIndex < 0) {
        list.push(next);
        return;
    }
    const existing = list[existingIndex];
    const merged = mergeProvisionSources(existing.sources, incomingSources);
    const keep = specificityScore(next) > specificityScore(existing) ? next : existing;
    list[existingIndex] = { ...keep, sources: merged, source: merged[0]?.name || keep.source };
};

/** Flatten component + plan provisions once. Plan-level rows often repeat component rules. */
const collectUniqueProvisions = (coverage) => {
    if (!coverage) return [];
    const unique = [];
    const ridersByName = riderIndex(coverage);
    for (const component of coverage.components || []) {
        const sources = sourcesForComponent(component, ridersByName);
        for (const provision of component.provisions || []) {
            addUniqueProvision(unique, {
                ...provision,
                slotName: component.slotName || inferSlotName(component) || provision.slotName,
                weight: component.weight ?? provision.weight,
                componentName: component.name,
            }, sources);
        }
    }
    for (const provision of coverage.planProvisions || []) {
        addUniqueProvision(unique, provision, [{ kind: "plan", name: "Plan" }]);
    }
    return unique;
};

const Coverage = () => {
    const [account, setAccount] = useState(null);
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [planOption, setPlanOption] = useState(null);
    const [planTab, setPlanTab] = useState("overview");
    const [answerFilter, setAnswerFilter] = useState("");
    const [serviceQuery, setServiceQuery] = useState("");
    const [selectedService, setSelectedService] = useState(null);
    const [riderTab, setRiderTab] = useState("chosen");
    const [riderQ, setRiderQ] = useState("");
    const [availableRiders, setAvailableRiders] = useState({ riders: [], total: 0, offset: 0 });
    const [ridersLoading, setRidersLoading] = useState(false);
    const [provisionModal, setProvisionModal] = useState(null);
    // Set when the user jumps from a browse view into Ask HRP via
    // "Check this service", carrying the code that makes the answer exact.
    const [askPrefill, setAskPrefill] = useState(null);

    const asOf = toIsoDate(asOfDate);
    const accountKey = account?.additional?.accountKey;
    const planKey = planOption?.value != null ? Number(planOption.value) : null;

    useEffect(() => {
        setPlanOption(null);
        setPlanTab("overview");
        setSelectedService(null);
        setAnswerFilter("");
        setServiceQuery("");
        setAvailableRiders({ riders: [], total: 0, offset: 0 });
        setProvisionModal(null);
    }, [accountKey]);

    const {
        data: planMap,
        isLoading: mapLoading,
        error: mapError,
    } = useQuery({
        queryKey: ["account-plan-map", accountKey, asOf],
        queryFn: () => fetchAccountPlanMap({ accountKey, asOfDate: asOf }),
        enabled: !!accountKey && !!asOf,
        staleTime: 0,
        retry: false,
    });

    const planOptions = useMemo(() => {
        const byKey = new Map();
        for (const acct of planMap?.accounts || []) {
            for (const p of acct.plans || []) {
                if (p.key == null || byKey.has(Number(p.key))) continue;
                byKey.set(Number(p.key), {
                    value: Number(p.key),
                    label: planOptionLabel(p),
                    additional: p,
                });
            }
        }
        return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [planMap]);

    // Drop selected plan if it is no longer effective for the as-of date.
    useEffect(() => {
        if (!planOption) return;
        if (mapLoading) return;
        if (!planOptions.some((o) => Number(o.value) === Number(planOption.value))) {
            setPlanOption(null);
            setPlanTab("overview");
        }
    }, [planOptions, planOption, mapLoading]);

    const {
        data: coverage,
        isLoading: coverageLoading,
        isFetching: coverageFetching,
        error: coverageError,
        status: coverageStatus,
    } = useQuery({
        queryKey: ["plan-coverage", planKey, asOf, accountKey],
        queryFn: () => fetchPlanCoverage({ planKey, asOfDate: asOf, accountKey }),
        enabled: !!planKey && !!asOf && !!accountKey,
        staleTime: 0,
        retry: false,
    });

    const { data: accumulatorSetup } = useQuery({
        queryKey: ["plan-accum-setup", planKey, asOf],
        queryFn: () => fetchPlanAccumulatorSetup({ planKey, asOfDate: asOf }),
        enabled: !!planKey && !!asOf,
        staleTime: 0,
        retry: false,
    });

    const allProvisions = useMemo(
        () => collectUniqueProvisions(coverage),
        [coverage],
    );

    const loadAvailableRiders = async (arg1, arg2) => {
        if (!planKey) return;
        const named = arg1 && typeof arg1 === "object";
        const offset = named ? (arg1.offset ?? 0) : (typeof arg2 === "number" ? arg2 : 0);
        const q = named ? (arg1.q ?? riderQ) : (arg1 ?? riderQ);
        setRidersLoading(true);
        try {
            const page = await fetchPlanRiders({
                planKey,
                asOfDate: asOf,
                scope: "available",
                q,
                limit: 40,
                offset,
            });
            setAvailableRiders({
                riders: page.riders || [],
                total: page.total || 0,
                offset: page.offset || 0,
                limit: page.limit || 40,
            });
        } catch {
            setAvailableRiders({ riders: [], total: 0, offset: 0 });
        } finally {
            setRidersLoading(false);
        }
    };

    useEffect(() => {
        if (planTab === "riders" && riderTab === "available" && planKey) {
            loadAvailableRiders({ offset: 0 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planTab, riderTab, planKey, asOf]);

    const openRiderModal = async (riderOrKey) => {
        const rider = riderOrKey && typeof riderOrKey === "object"
            ? riderOrKey
            : { key: riderOrKey };
        if (!planKey || rider?.key == null) return;
        setProvisionModal({
            title: rider.name,
            subtitle: rider.hccId || "",
            loading: true,
            provisions: [],
            components: [],
        });
        try {
            const res = await fetchPlanRiderDetail({
                planKey,
                riderKey: rider.key,
                asOfDate: asOf,
            });
            const detail = res.rider || res;
            const components = detail.components || [];
            const provisions = [];
            for (const component of components) {
                const sources = component.name ? [{ kind: "component", name: component.name }] : [];
                for (const provision of component.provisions || []) {
                    addUniqueProvision(provisions, provision, sources);
                }
            }
            setProvisionModal({
                title: detail.name || rider.name,
                subtitle: detail.hccId || rider.hccId || "",
                loading: false,
                provisions,
                components,
            });
        } catch (e) {
            setProvisionModal({
                title: rider.name,
                subtitle: e.message || "Failed to load rider",
                loading: false,
                provisions: [],
                components: [],
            });
        }
    };

    const onSelectVersion = (v) => {
        if (!v?.versionEffDate) return;
        const [y, m, d] = String(v.versionEffDate).split("-").map(Number);
        if (!y) return;
        setAsOfDate(new Date(y, m - 1, d));
    };

    const clearFilters = () => {
        setAccount(null);
        setPlanOption(null);
        setAsOfDate(new Date());
        setPlanTab("overview");
        setSelectedService(null);
        setAnswerFilter("");
        setServiceQuery("");
        setAvailableRiders({ riders: [], total: 0, offset: 0 });
        setProvisionModal(null);
    };

    const coverageReady = coverage && !coverage.error && coverage.plan;

    return (
        <div className="container coverage">
            <p className="title">Coverage</p>
            <p className="sub-title">View benefit plan provisions, accumulators, networks, and riders</p>

            <div className="coverage__filters">
                <div className="coverage__field">
                    <label>Account</label>
                    <AsyncPaginate
                        placeholder="Select an account"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        debounceTimeout={300}
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={setAccount}
                        styles={{
                            ...customStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 200 }),
                        }}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        isClearable
                    />
                </div>
                {account && (
                    <div className="coverage__field">
                        <label>Benefit plan</label>
                        <Select
                            placeholder={
                                mapLoading
                                    ? "Loading plans…"
                                    : planOptions.length
                                        ? "Select a benefit plan"
                                        : "No plans for this as-of date"
                            }
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={planOption}
                            onChange={(opt) => {
                                setPlanOption(opt);
                                setPlanTab("overview");
                            }}
                            options={planOptions}
                            isDisabled={mapLoading || !planOptions.length}
                            isLoading={mapLoading}
                            isClearable
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={{
                                ...customStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 200 }),
                            }}
                        />
                    </div>
                )}
                <div className="coverage__field coverage__field--date">
                    <label>As of</label>
                    <DatePicker
                        selected={asOfDate}
                        onChange={(d) => setAsOfDate(d || new Date())}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="As of date"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                        popperPlacement="bottom-start"
                        popperClassName="date-picker-popper"
                        popperProps={{ strategy: "fixed" }}
                    />
                </div>
                <button type="button" className="coverage__clear-btn danger" onClick={clearFilters}>
                    Clear filters
                </button>
            </div>

            {!account && (
                <p className="coverage__hint">Select an account, then a benefit plan effective on the as-of date.</p>
            )}
            {account && (mapError || planMap?.error) && (
                <span className="danger">
                    Error: {mapError?.message || planMap?.error || "Failed to load account plans"}
                </span>
            )}
            {account && !mapLoading && !mapError && !planMap?.error && !planOptions.length && (
                <p className="coverage__hint">No benefit plans for this account on the selected as-of date.</p>
            )}

            {planKey && (coverageLoading || coverageFetching) && <div className="loader" />}
            {planKey && (coverageStatus === "error" || coverageError) && (
                <span className="danger">Error: {coverageError?.message || "Failed to load coverage"}</span>
            )}
            {coverage?.error && <span className="danger">{coverage.error}</span>}

            {coverageReady && (
                <PlanView
                    coverage={coverage}
                    asOf={asOf}
                    answerFilter={answerFilter}
                    setAnswerFilter={setAnswerFilter}
                    planTab={planTab}
                    setPlanTab={setPlanTab}
                    serviceQuery={serviceQuery}
                    setServiceQuery={setServiceQuery}
                    selectedService={selectedService}
                    setSelectedService={setSelectedService}
                    riderTab={riderTab}
                    setRiderTab={setRiderTab}
                    availableRiders={availableRiders}
                    riderQ={riderQ}
                    setRiderQ={setRiderQ}
                    loadAvailableRiders={loadAvailableRiders}
                    ridersLoading={ridersLoading}
                    openRiderModal={openRiderModal}
                    provisionModal={provisionModal}
                    setProvisionModal={setProvisionModal}
                    accumulatorSetup={accumulatorSetup?.error ? null : accumulatorSetup}
                    allProvisions={allProvisions}
                    onSelectVersion={onSelectVersion}
                    askPrefill={askPrefill}
                    setAskPrefill={setAskPrefill}
                />
            )}
        </div>
    );
};

export default Coverage;
